import { Cell, Flow, Grid, Icon, Modal, ModalBody, ModalFooter, ModalFooterButton, ModalHeader, Text } from 'bold-ui'
import { Box } from 'components/Box'
import { Form, PecFormApi, SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import { AnimaisDomicilioSelectField } from 'components/form/field/select/AnimaisDomicilioSelectField/AnimaisDomicilioSelectField'
import {
  TipoAbastecimentoAguaSelectField,
  TipoDomicilioSelectField,
  TipoEscoamentoSanitarioSelectField,
  TipoImovelSelectField,
  TipoMaterialParedeSelectField,
  TipoPosseTerraSelectField,
  TipoSituacaoMoradiaSelectField,
  TipoTratamentoAguaSelectField,
} from 'components/form/field/select/CondicoesMoradiaSelectFields'
import { TipoAcessoDomicilioSelectField } from 'components/form/field/select/CondicoesMoradiaSelectFields/TipoAcessoDomicilioSelectField'
import { TipoDestinoLixoSelectField } from 'components/form/field/select/CondicoesMoradiaSelectFields/TipoDestinoLixoSelectField'
import { TipoEnergiaEletricaSelectField } from 'components/form/field/select/CondicoesMoradiaSelectFields/TipoEnergiaEletricaSelectField'
import { PeriodoUltimaAtualizacaoSelectField } from 'components/form/field/select/PeriodoUltimaAtualizacaoSelectField'
import { PeriodoUltimaVisitaSelectField } from 'components/form/field/select/PeriodoUltimaVisitaSelectField'
import { StatusCadastroSelectField } from 'components/form/field/select/StatusCadastroSelectField'
import { TipoLocalizacaoRadioGroupField } from 'components/form/field/TipoLocalizacaoRadioGroupField'
import { TipoEnderecoEnum, TipoLocalizacaoEnum } from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'
import { tipoEnderecoIndigena } from 'view/cadastro-imovel/model-cadastroImovel'
import { TerritorioViewTabEnum, VisualizacaoMicroareasFilterFormModel } from 'view/visualizacaoTerritorial/model'
import { createVisualizacaoMicroareaFilterCalculator } from 'view/visualizacaoTerritorial/utils/microarea-filter-calculator'

import { isMicroareasFiltrosDefault } from './utils-microareasFilter'

interface MicroareasFilterModalProps {
  tabContext: TerritorioViewTabEnum
  open: boolean
  tipoEndereco: TipoEnderecoEnum
  initialValues: VisualizacaoMicroareasFilterFormModel | undefined
  onModalClose(): void
  onSubmit(values: VisualizacaoMicroareasFilterFormModel): void
}

const meta = metaPath<VisualizacaoMicroareasFilterFormModel>()

export function MicroareasFilterModal(props: MicroareasFilterModalProps) {
  const { tabContext, open, tipoEndereco, initialValues, onModalClose, onSubmit } = props

  const handleReset = (form: PecFormApi<VisualizacaoMicroareasFilterFormModel>) => {
    form.reset({})
  }

  return (
    <Form<VisualizacaoMicroareasFilterFormModel>
      render={({ form, values, handleSubmit }) => (
        <Modal open={open} onClose={onModalClose} closeOnBackdropClick={false}>
          <ModalHeader
            title={tabContext === TerritorioViewTabEnum.LISTA_DE_IMOVEIS ? 'Filtros' : 'Filtros avançados'}
          />
          <ModalBody>
            <Grid>
              <Cell size={6}>
                <StatusCadastroSelectField label='Situação do cadastro do imóvel' name={meta.statusCadastroImovel} />
              </Cell>
              <Cell size={6}>
                <SimNaoRadioGroupField clearable name={meta.possuiInconsistencia} label='Possui inconsistência?' />
              </Cell>
              <Cell size={6}>
                <PeriodoUltimaAtualizacaoSelectField
                  label='Atualizado há'
                  name={meta.isCadastroAtualizadoMenosDeUmAno}
                />
              </Cell>
              <Cell size={6}>
                <TipoLocalizacaoRadioGroupField
                  name={meta.condicoesMoradia.tipoLocalizacao}
                  clearable
                  label='Localização'
                />
              </Cell>
              {!tipoEnderecoIndigena.includes(tipoEndereco) ? (
                <Cell size={6}>
                  <PeriodoUltimaVisitaSelectField label='Última visita há' name={meta.isImovelVisitadoMenosDeUmMes} />
                </Cell>
              ) : (
                <Cell size={6} />
              )}
              <Cell size={6}>
                <TipoImovelSelectField label='Tipo de imóvel' name={meta.tipoImovel} />
              </Cell>

              <Cell size={12} />

              <Cell size={12}>
                <Text fontSize={1} fontWeight='bold'>
                  Filtrar por característica do imóvel
                </Text>
              </Cell>
              <Cell size={12}>
                <Box>
                  <Grid>
                    <Cell size={6}>
                      <TipoDomicilioSelectField label='Tipo de domicílio' name={meta.condicoesMoradia.tipoDomicilio} />
                    </Cell>
                    <Cell size={6}>
                      <TipoMaterialParedeSelectField
                        label='Material predominante'
                        name={meta.condicoesMoradia.tipoMaterialParede}
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoSituacaoMoradiaSelectField
                        label='Situação de moradia/posse de terra'
                        name={meta.condicoesMoradia.tipoSituacaoMoradia}
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoAbastecimentoAguaSelectField
                        label='Abastecimento de água'
                        name={meta.condicoesMoradia.tipoAbastecimentoAgua}
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoPosseTerraSelectField
                        label='Condições de posse e uso de terra'
                        name={meta.condicoesMoradia.tipoPosseTerra}
                        disabled={values?.condicoesMoradia?.tipoLocalizacao !== TipoLocalizacaoEnum.RURAL}
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoTratamentoAguaSelectField
                        label='Água para consumo'
                        name={meta.condicoesMoradia.tipoTratamentoAgua}
                        showItensLegados={true}
                      />
                    </Cell>
                    <Cell size={6}>
                      <SimNaoRadioGroupField
                        clearable
                        name={meta.condicoesMoradia.isEnergiaEletricaDisponivel}
                        label='Possui energia elétrica?'
                      />
                    </Cell>
                    <Cell size={6}>
                      <SimNaoRadioGroupField
                        clearable
                        name={meta.animaisImovelFilter.possuiAnimais}
                        label='Possui animais?'
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoEnergiaEletricaSelectField
                        name={meta.condicoesMoradia.tipoOrigemEnergiaEletrica}
                        label='Tipo de energia elétrica'
                        disabled={values?.condicoesMoradia?.isEnergiaEletricaDisponivel !== SimNaoEnum.SIM}
                      />
                    </Cell>
                    <Cell size={6}>
                      <AnimaisDomicilioSelectField
                        label='Tipos de animais no domicílio'
                        name={meta.animaisImovelFilter.animaisNoImovel}
                        disabled={values?.animaisImovelFilter?.possuiAnimais !== SimNaoEnum.SIM}
                        multiple
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoAcessoDomicilioSelectField
                        label='Tipo de acesso'
                        name={meta.condicoesMoradia.tipoAcessoDomicilio}
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoDestinoLixoSelectField
                        label='Destino do lixo'
                        name={meta.condicoesMoradia.tipoDestinoLixo}
                        showItensLegados={true}
                      />
                    </Cell>
                    <Cell size={6}>
                      <TipoEscoamentoSanitarioSelectField
                        label='Escoamento de banheiro'
                        name={meta.condicoesMoradia.tipoEscoamentoSanitario}
                      />
                    </Cell>
                    <Cell size={6} />
                  </Grid>
                </Box>
              </Cell>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Flow direction='horizontal' justifyContent='space-between'>
              <ModalFooterButton
                kind='primary'
                skin='outline'
                onClick={() => handleReset(form)}
                disabled={isMicroareasFiltrosDefault(values)}
              >
                <Flow direction='horizontal' gap={0.5}>
                  <Icon icon='undo' />
                  Limpar filtros
                </Flow>
              </ModalFooterButton>
              <Flow direction='horizontal'>
                <ModalFooterButton onClick={onModalClose}>Cancelar</ModalFooterButton>
                <ModalFooterButton kind='primary' onClick={handleSubmit}>
                  {tabContext === TerritorioViewTabEnum.LISTA_DE_IMOVEIS ? 'Filtrar' : 'Atualizar busca'}
                </ModalFooterButton>
              </Flow>
            </Flow>
          </ModalFooter>
        </Modal>
      )}
      initialValues={initialValues}
      decorators={createVisualizacaoMicroareaFilterCalculator}
      onSubmit={onSubmit}
    />
  )
}
